import React from "react";
import { Box, Typography, Paper, Grid, Button, Divider } from "@mui/material";
import { AccountBalance, Visibility } from "@mui/icons-material";
import { CaseWhenBenefit } from "services/GraphQl";
import FormattedCurrency from "components/WhenBenefit/FormattedCurrency";

interface WhenBenefitDetailsCardProps {
  whenBenefit: CaseWhenBenefit;
  showBankInfo: () => void;
  bankInfoData: any;
}

const WhenBenefitDetailsCard: React.FC<WhenBenefitDetailsCardProps> = ({ whenBenefit, showBankInfo, bankInfoData }) => {
  const detail = whenBenefit.detail || {};
  const useOnCobra = whenBenefit.useOnCobra;
  const bankInfo = bankInfoData?.getUserBankInfo;

  const benefitItems = [
    {
      label: "Total When Benefit Amount",
      value: detail.total || 0,
      color: "#1f2937",
      isTotal: true,
    },
    {
      label: "Amount Remaining",
      value: detail.amountRemaining || 0,
      color: "#10b981",
    },
    {
      label: "Amount Pending",
      value: detail.amountPending || 0,
      color: "#f59e0b",
    },
    {
      label: "Amount Spent",
      value: detail.amountSpent || 0,
      color: "#6366f1",
    },
    {
      label: "Amount Forfeited",
      value: detail.amountForfeited || 0,
      color: "#ef4444",
    },
  ];

  return (
    <Box sx={{ width: "100%", p: 2 }}>
      <Paper
        sx={{
          p: 3,
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
          border: "1px solid #e0e0e0",
          borderRadius: 2,
        }}
      >
        {/* Header */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold", mb: 0.5, fontSize: "1.1rem" }}>
            Case When Benefit Details
          </Typography>
        </Box>

        {/* Total Amount and COBRA Usage - Featured Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
                border: "1px solid #e5e7eb",
                height: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.875rem" }}>
                {benefitItems[0].label}
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  color: benefitItems[0].color,
                }}
              >
                <FormattedCurrency value={benefitItems[0].value} />
              </Typography>
            </Box>
          </Grid>

          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                bgcolor: "#f8f9fa",
                borderRadius: 2,
                p: 2,
                border: "1px solid #e5e7eb",
                height: "100%",
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5, fontSize: "0.875rem" }}>
                When Benefit Use On COBRA
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: "bold",
                  color: "#1f2937",
                }}
              >
                {useOnCobra ? "Allowed" : "Not Allowed"}
              </Typography>
            </Box>
          </Grid>
        </Grid>

        {/* Breakdown Grid */}
        <Grid container spacing={2}>
          {benefitItems.slice(1).map((item, index) => (
            <Grid item xs={6} sm={3} key={index}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1,
                  border: "1px solid #e5e7eb",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 1, fontWeight: "500", fontSize: "0.8rem" }}
                >
                  {item.label}
                </Typography>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: "bold",
                    color: item.color,
                    fontSize: "1rem",
                  }}
                >
                  <FormattedCurrency value={item.value} />
                </Typography>
              </Box>
            </Grid>
          ))}
        </Grid>

        <Divider sx={{ my: 3 }} />

        {/* Bank Info Section */}
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <AccountBalance sx={{ mr: 1, color: "#6b7280" }} />
              <Typography variant="h6" sx={{ fontWeight: "bold", fontSize: "1rem" }}>
                Bank Information
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              startIcon={<Visibility />}
              onClick={showBankInfo}
              sx={{
                borderColor: "#3b82f6",
                color: "#3b82f6",
                "&:hover": {
                  borderColor: "#2563eb",
                  backgroundColor: "#eff6ff",
                },
              }}
            >
              {bankInfo ? "Refresh" : "Show Bank Info"}
            </Button>
          </Box>

          {bankInfo && (
            <Box
              sx={{
                p: 2,
                borderRadius: 1,
                border: "1px solid #e5e7eb",
                bgcolor: "#f9fafb",
              }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                    Account Holder
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {bankInfo.nameOnAccount || "—"}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                    Account Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {bankInfo.accountNumber || "—"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem", mb: 0.5 }}>
                    Routing Number
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: "500" }}>
                    {bankInfo.routingNumber || "—"}
                  </Typography>
                </Grid>
              </Grid>
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default WhenBenefitDetailsCard;
